/**
 * Locale for the formatters that print prose figures — file sizes, counts, dates.
 *
 * A module variable rather than an argument threaded through every call site, kept
 * in step by the i18n module on every language change. It is deliberately *not* used
 * by the timing formatters below: a lap time is 1:23.456 in every language, because
 * that is how every timing screen and every telemetry tool writes one, and a table
 * of them with commas in the middle would be harder to read rather than easier.
 */
let locale = "en";

export function setFormatLocale(code: string) {
  locale = code;
}

export function formatLapTime(ms: number | null): string {
  if (ms === null || !Number.isFinite(ms)) return "—";
  const totalSeconds = ms / 1000;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds - minutes * 60;
  return `${minutes}:${seconds.toFixed(3).padStart(6, "0")}`;
}

/** Signed gap against a reference lap, the way a timing screen shows it. */
export function formatDelta(ms: number | null, referenceMs: number | null): string {
  if (ms === null || referenceMs === null) return "";
  const delta = (ms - referenceMs) / 1000;
  if (Math.abs(delta) < 0.0005) return "—";
  return `${delta > 0 ? "+" : ""}${delta.toFixed(3)}`;
}

export function formatDuration(ms: number | null): string {
  if (ms === null) return "—";
  const totalSeconds = Math.round(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
}

export function formatBytes(bytes: number | null): string {
  if (bytes === null) return "—";
  const units = ["B", "KB", "MB", "GB"];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  const digits = value < 10 && unit > 0 ? 1 : 0;
  return `${formatNumber(value, digits)} ${units[unit]}`;
}

/** A plain number in the reader's language, for prose and for wide columns. */
export function formatNumber(value: number, digits = 0): string {
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(value);
}

export function formatCount(value: number): string {
  return new Intl.NumberFormat(locale).format(value);
}

/** LMU reports ground speed in km/h already, so there is nothing to convert. */
export function formatSpeedKph(kph: number | null): string {
  if (kph === null) return "—";
  return `${kph.toFixed(1)} km/h`;
}

/**
 * Temperature scale, which is a preference rather than a translation: plenty of
 * people read the site in English and think in Celsius, and a couple do the
 * reverse. So it is chosen and remembered on its own, not inferred from language.
 */
export type TemperatureUnit = "c" | "f";

export function formatTemperature(
  celsius: number | null,
  unit: TemperatureUnit,
): string {
  if (celsius === null || !Number.isFinite(celsius)) return "—";
  const value = unit === "f" ? celsius * 1.8 + 32 : celsius;
  return `${formatNumber(value, 1)} °${unit.toUpperCase()}`;
}

export function formatDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString(locale);
}

/** Day and month only, for columns where the year would not earn its width. */
export function formatDay(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(locale, { day: "numeric", month: "short" });
}

/** Day, month and time, for telling apart two sessions driven on the same day. */
export function formatDayTime(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString(locale, {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function formatFullDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(locale);
}

/**
 * A channel's quantisation scale already states the precision it was stored at, so
 * it doubles as the number of decimals worth printing. Anything beyond that is
 * inventing digits the file never held.
 */
export function formatChannelValue(value: number | null, scale: number): string {
  if (value === null || !Number.isFinite(value)) return "—";
  const decimals = Math.min(3, Math.max(0, Math.round(Math.log10(scale))));
  return value.toFixed(decimals);
}

/** Signed seconds, for a delta read off a trace rather than a lap total. */
export function formatSignedSeconds(seconds: number | null): string {
  if (seconds === null || !Number.isFinite(seconds)) return "—";
  return `${seconds > 0 ? "+" : ""}${seconds.toFixed(3)}`;
}
